import React from 'react';

import StickyButton from '@/components/atom/buttons/StickyButton';
import ClickLogger from '@/components/common/ClickLogger';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import ReportSelectModal from '@/components/molecules/modal/select-modal/report-select-modal';
import StickyAppHeader from '@/components/molecules/sticky-app-header';
import { DoctorDetailLog } from '@/constants/logs/doctor';
import DNSlideTab from '@/design-components/organisms/dnslide-tab';
import { EWebTreatmentRequestLog } from '@/entities/diagnosis/libs';
import useReviewReportModal from '@/hooks/review/useReviewReportModal';

import { TreatmentRequestDoctor } from '@/models/diagnosis';
import { CustomPageProps } from '@/types/app';
import { DoctorDetail } from '@/types/doctor';
import {
  BenefitPrice,
  DoctorDetailSkeleton,
  DoctorIntroduction,
  DoctorReview,
  NonBenefitPrice,
} from '@/views/doctor/detail';
import { DoctorDetailTabType } from '@/views/doctor/detail/lib';
import DoctorProfile from '@/views/doctor/detail/ui/doctor-summary/DoctorProfile';
import DoctorReviewAndDepartments from '@/views/doctor/detail/ui/doctor-summary/DoctorReviewAndDepartments';
import { useDoctorReviewsViewModel } from '@/views/doctor/reviews';
import { Button } from 'doctornow-design-system';
import useDoctorDetailViewModel from '../hooks/useDoctorDetailViewModel';

interface Props extends CustomPageProps {
  handleSelectDoctor?: (doctor?: TreatmentRequestDoctor, doctorPublicId?: string, doctorDetail?: DoctorDetail) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}
const DoctorDetailInfo = ({ client, handleSelectDoctor, scrollContainerRef }: Props) => {
  const doctorDetailViewModel = useDoctorDetailViewModel();
  const doctorReviewsViewModel = useDoctorReviewsViewModel();
  const { showReportModal, onCloseReportModal, reportItems } = useReviewReportModal();

  const calculateScrollDepth = (): number => {
    if (!scrollContainerRef?.current) return 0;

    const container = scrollContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    const maxScroll = scrollHeight - clientHeight;

    if (maxScroll <= 0) return 0;

    const depth = Math.round((scrollTop / maxScroll) * 100);
    return Math.min(Math.max(depth, 0), 100);
  };

  const onSelectDoctorByWebTelemedicine = () => {
    handleSelectDoctor?.(
      doctorDetailViewModel.treatmentRequestDoctor,
      doctorDetailViewModel.doctorPublicId,
      doctorDetailViewModel.doctorDetail
    );
  };

  const renderDoctorInfoSections = () => {
    return doctorDetailViewModel.getTabs().map((tab) => {
      switch (tab.value) {
        case DoctorDetailTabType.BenefitPrice:
          return <BenefitPrice key={tab.value} title={doctorDetailViewModel.getBenefitPriceSectionTitle()} />;
        case DoctorDetailTabType.NonBenefitPrice:
          return (
            <NonBenefitPrice
              key={tab.value}
              getNonBenefitPriceCategories={doctorDetailViewModel.getNonBenefitPriceCategories}
              isCategorySelected={doctorDetailViewModel.isCategorySelected}
              isLoggedIn={doctorDetailViewModel.isLoggedIn}
              isPointPriceVisible={doctorDetailViewModel.isPointPriceVisible}
              togglePointPrice={doctorDetailViewModel.togglePointPrice}
              isWithDispensation={doctorDetailViewModel.isWithDispensation}
              toggleWithDispensation={doctorDetailViewModel.toggleWithDispensation}
              shouldShowPointTermInfo={doctorDetailViewModel.shouldShowPointTermInfo}
              getNonBenefitTreatmentPriceListByCategory={
                doctorDetailViewModel.getNonBenefitTreatmentPriceListByCategory
              }
              isDietCategoryFor={doctorDetailViewModel.isDietCategoryFor}
            />
          );
        case DoctorDetailTabType.DoctorProfile:
          return (
            <DoctorIntroduction
              key={tab.value}
              hasNoNotice={doctorDetailViewModel.hasNoNotice}
              hasNoBadge={doctorDetailViewModel.hasNoBadge}
              hasNoCareer={doctorDetailViewModel.hasNoCareer}
              notice={doctorDetailViewModel.doctorDetail?.notice}
              badges={doctorDetailViewModel.doctorDetail?.badges}
              doctor={doctorDetailViewModel.doctorDetail?.doctor}
              getCareerDisplayText={doctorDetailViewModel.getCareerDisplayText}
              doctorTreatments={doctorDetailViewModel.getDoctorTreatments()}
              doctorDuties={doctorDetailViewModel.getDoctorDuties()}
              callHospital={doctorDetailViewModel.callHospital}
              shouldShowTreatmentSection={doctorDetailViewModel.shouldShowTreatmentSection}
              hospital={doctorDetailViewModel.doctorDetail?.hospital}
              diagnosisContactType={doctorDetailViewModel.diagnosisContactType}
              openMap={() => {}}
            />
          );
        case DoctorDetailTabType.Review:
          return (
            <DoctorReview
              key={tab.value}
              isMobile={client.isMobile}
              doctorReviewsViewModel={doctorReviewsViewModel}
              shouldShowAllReviewsButton={false}
            />
          );
        default:
          return null;
      }
    });
  };

  if (doctorDetailViewModel.isDoctorDetailLoading || !doctorDetailViewModel.doctorDetail) {
    return (
      <TemplateLayout.Wrapper>
        <TemplateLayout.StickyHeader>
          <StickyAppHeader isAbsolute backgroundColor={'transparent'} />
        </TemplateLayout.StickyHeader>
        <TemplateLayout.Content>
          <DoctorDetailSkeleton />
        </TemplateLayout.Content>
      </TemplateLayout.Wrapper>
    );
  }

  return (
    <TemplateLayout.Wrapper>
      <TemplateLayout.Content>
        <div className='min-h-[calc(100vh-60px)]'>
          {!client.isMobile && <div className='pt-[40px]' />}
          <div className='flex flex-col items-center'>
            <div className='flex flex-col gap-[12px] self-stretch pb-[32px]'>
              <DoctorProfile
                profileImageUrl={doctorDetailViewModel.doctorDetail.doctor.profileImageUrl}
                hospitalName={doctorDetailViewModel.doctorDetail.hospital.name}
                doctorName={doctorDetailViewModel.doctorDetail.doctor.name}
                doctorMedicineType={doctorDetailViewModel.doctorDetail.doctor.medicineType}
                isFamilyDoctor={doctorDetailViewModel.isFamilyDoctor}
                doctorId={doctorDetailViewModel.doctorDetail.doctor.id}
                handleUpdateFamilyDoctor={doctorDetailViewModel.handleUpdateFamilyDoctor}
                getDoctorDisplayName={doctorDetailViewModel.getDoctorDisplayName}
              />
              <div className='px-[24px]'>
                <DoctorReviewAndDepartments
                  doctorDetail={doctorDetailViewModel.doctorDetail}
                  doctorDepartments={doctorDetailViewModel.doctorDetail?.doctor.departments ?? []}
                  totalReviewCount={doctorReviewsViewModel.totalCount}
                  hasReview={doctorReviewsViewModel.hasReview}
                  getDisplayAverageRating={doctorDetailViewModel.getDisplayAverageRating}
                  goToReview={doctorDetailViewModel.goToReview}
                />
              </div>
            </div>
          </div>
          <section className='sticky top-0 z-[2] bg-white'>
            <DNSlideTab value={doctorDetailViewModel.selectedTab}>
              {doctorDetailViewModel.getTabs().map((tab) => (
                <ClickLogger
                  key={tab.key}
                  params={{
                    eventName:
                      tab.value === DoctorDetailTabType.Review
                        ? DoctorDetailLog.DOCTOR_DETAIL_REVIEW_CLICK
                        : DoctorDetailLog.DOCTOR_DETAIL_INFO_CLICK,
                    payload: {
                      tabName: doctorDetailViewModel.getTabName(tab.value, tab.label),
                      tabValue: tab.value,
                      doctorId: doctorDetailViewModel.doctorDetail?.doctor.id ?? '',
                    },
                  }}
                >
                  <DNSlideTab.Item
                    label={tab.label}
                    isActive={tab.value === doctorDetailViewModel.selectedTab}
                    onClick={() => doctorDetailViewModel.onClickTab(tab)}
                  />
                </ClickLogger>
              ))}
            </DNSlideTab>
          </section>
          {renderDoctorInfoSections()}
          <ReportSelectModal isVisible={showReportModal} selectList={reportItems} onClose={onCloseReportModal} />
        </div>
        <StickyButton>
          <ClickLogger
            params={{
              eventName: EWebTreatmentRequestLog.ClickTelemedicineRequestInDoctorDetail,

              payload: () => ({
                doctorId: doctorDetailViewModel.doctorDetail?.doctor.id ?? '',
                doctorName: doctorDetailViewModel.doctorDetail?.doctor.name ?? '',
                scrollDepth: calculateScrollDepth(),
                categoryId: doctorDetailViewModel.categoryId ?? '',
                diagnosisType: doctorDetailViewModel.diagnosisType ?? '',
              }),
            }}
          >
            <div className='flex w-full flex-1 flex-col'>
              <Button size={'xl'} onClick={onSelectDoctorByWebTelemedicine}>
                {doctorDetailViewModel.doctorDetail.doctor.name} 의사에게 진료받기
              </Button>

              <span className='typography-ui-13m w-full pt-[12px] text-center text-gray-400'>
                진료가 끝나기 전에는 진료비가 결제되지 않아요
              </span>
            </div>
          </ClickLogger>
        </StickyButton>
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default DoctorDetailInfo;
